import { Editor } from '~/Components/Editor';
import classNames from 'classnames';
import { connect, FormikContextType, getIn } from 'formik';
import React, { useCallback, useRef } from 'react';
import { LANGUAGES } from '~/Constants/Languages.constants';
import { FONT_SIZE_LARGE } from '~/Constants/UI.constants';
import { TextField, Typography } from '~/UIKit';
import { useTextEditor, useTextArea } from '.';
import { makeStyles } from '~/UIKit/Style';
import { ItemCreationToolTip, ItemCreationToolTipProps } from '../ItemCreationToolTip';

interface ITextEditorProps {
  fieldName: string;
  firstItem?: boolean;
  title?: string;
  noMarkup?: boolean;
  noToolbarSettings?: boolean;
  tooltip?: Omit<ItemCreationToolTipProps, 'containerRef'> | false;
  valuesAsArray?: boolean;
  isLanguageSelectorAlwaysOn?: boolean;
  className?: string;
  withoutTranslation?: boolean;
}

const useStyles = (firstItem: boolean, hasError: boolean, isLanguageSelectorAlwaysOn?: boolean) =>
  makeStyles((theme) => ({
    DescriptionBoxWrapper: {
      padding: firstItem ? '30px 16px 16px' : '0 16px',
      [theme.breakpoints.up('md')]: {
        margin: 0,
        marginTop: firstItem ? 30 : 0,
        marginBottom: 30,
      },
      '& .tox-toolbar__primary': {
        justifyContent: isLanguageSelectorAlwaysOn ? 'flex-end' : 'none',
      },
    },
    DescriptionTitle: {
      fontWeight: 'bolder',
      fontSize: FONT_SIZE_LARGE,
      display: 'flex',
      alignItems: 'center',
    },
    DescriptionBoxHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: 15,
      marginTop: -9,
    },
  }));

export const TextEditor = connect<ITextEditorProps, any>(
  ({
    fieldName,
    firstItem,
    title,
    noMarkup,
    noToolbarSettings,
    formik,
    tooltip,
    valuesAsArray,
    isLanguageSelectorAlwaysOn,
    className,
    withoutTranslation,
  }: ITextEditorProps & { formik: FormikContextType<any> }) => {
    const containerRef = useRef(null);
    const defaultValues = getIn(formik.values, fieldName);
    const fieldHasError = getIn(formik.errors, fieldName);

    const { classes } = useStyles(
      firstItem || false,
      !!fieldHasError,
      isLanguageSelectorAlwaysOn,
    )();

    const {
      setCurrentText,
      defaultText,
      currentText,
      changeLanguage,
      providerLanguage,
      currentLanguage,
    } = useTextEditor({
      onChange: formik.setFieldValue,
      fieldName,
      defaultValues,
      valuesAsArray,
    });

    const {
      setCurrentText: setCurrentTextareaValue,
      currentText: currentTextareaValue,
    } = useTextArea({
      onChange: formik.setFieldValue,
      fieldName,
      defaultValue: defaultValues,
    });

    const handleTextareaChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      setCurrentTextareaValue(event.target.value);
    };

    const availableLanguagesForText =
      !defaultValues || typeof defaultValues !== 'object' || !isLanguageSelectorAlwaysOn
        ? []
        : Object.keys(defaultValues).filter((language) => defaultValues[language]?.trim() !== '');

    const generateToolbar = () => {
      if (!noMarkup && !noToolbarSettings) {
        return `undo redo | blocks | bold italic | \
          alignleft aligncenter alignright | \
          bullist numlist outdent indent | link`;
      }
      return '';
    };

    const generateLanguages = () => {
      if (availableLanguagesForText.length > 1) {
        return availableLanguagesForText.map((language) => ({
          type: 'menuitem' as any,
          text: language.toUpperCase(),
          onAction: changeLanguage(language.toUpperCase()),
        }));
      }
      return LANGUAGES.map((language) => ({
        type: 'menuitem' as any,
        text: language.toUpperCase(),
        onAction: changeLanguage(language.toUpperCase()),
      }));
    };

    const onEditorTextChange = useCallback(
      (text: string) => {
        if (availableLanguagesForText.length > 1 && providerLanguage !== currentLanguage) {
          return;
        }
        setCurrentText(text);
      },
      [availableLanguagesForText, providerLanguage, currentLanguage, setCurrentText],
    );

    return (
      <div className={classes.DescriptionBoxWrapper} ref={containerRef}>
        <div className={classes.DescriptionBoxHeader}>
          <Typography className={classes.DescriptionTitle}>
            {title} <ItemCreationToolTip {...tooltip} containerRef={containerRef} />
          </Typography>
        </div>
        {noMarkup ? (
          <TextField
            className={classNames(classes.Textarea, classes.TextField, className)}
            newDesign
            value={currentTextareaValue}
            multiline
            onChange={handleTextareaChange}
          />
        ) : (
          <Editor
            initialValue={defaultText}
            value={withoutTranslation ? defaultValues : currentText}
            onEditorChange={(value: string) => {
              withoutTranslation
                ? formik.setFieldValue(fieldName, value)
                : onEditorTextChange(value);
            }}
            init={{
              height: 400,
              menubar: false,
              entity_encoding: 'raw',
              plugins: !noMarkup
                ? [
                    'advlist',
                    'autolink',
                    'lists',
                    'link',
                    'image',
                    'charmap',
                    'preview',
                    'anchor',
                    'help',
                    'searchreplace',
                    'media',
                    'table',
                    'quickbars',
                  ]
                : [],
              statusbar: false,
              branding: false,
              toolbar: generateToolbar(),
              setup: (editor: any) => {
                editor.ui.registry.addMenuButton('languageSelector', {
                  icon: 'language',
                  fetch: (callback: any) => callback(generateLanguages()),
                });
              },
            }}
          />
        )}
      </div>
    );
  },
);
